import { cn } from "@coideo/barrani";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  CalculatorIcon,
  CollectionIcon,
  LogoutIcon,
  MenuAlt2Icon,
  PrinterIcon,
  RefreshIcon,
  ShoppingBagIcon,
  TagIcon,
  XIcon,
  TranslateIcon,
} from "@heroicons/react/outline";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { useAuth } from "context/auth-context";
import { Link } from "lib";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { Fragment, type ReactNode, useState } from "react";
import { PERMISSIONS } from "utils/auth-client";

import { CashDrawerDetail, CashDrawerSlider, OpenCashDrawer } from "../CashDrawer";

import Logo from "./Logo";

const enum UserType {
  BACKOFFICE,
  POS,
}

const navigation = [
  {
    key: "sell" as const,
    href: "/",
    icon: TagIcon,
    permissions: [],
    users: [UserType.POS, UserType.BACKOFFICE],
  },
  {
    key: "exchange" as const,
    href: "/exchange",
    icon: RefreshIcon,
    permissions: [PERMISSIONS.EXCHANGE],
    users: [UserType.POS],
  },
  {
    key: "latestPurchase" as const,
    href: "/purchases",
    icon: ShoppingBagIcon,
    permissions: [],
    users: [UserType.POS],
  },
  {
    key: "sitef" as const,
    href: "/configuration/sitef",
    icon: CalculatorIcon,
    permissions: [PERMISSIONS.ADMIN_SHOP],
    users: [UserType.POS, UserType.BACKOFFICE],
  },
  {
    key: "printer" as const,
    href: "/configuration/printer",
    icon: PrinterIcon,
    permissions: [],
    users: [UserType.POS, UserType.BACKOFFICE],
  },
  {
    key: "language" as const,
    href: "/configuration/language",
    icon: TranslateIcon,
    permissions: [],
    users: [UserType.POS, UserType.BACKOFFICE],
  },
];

function CashDrawer() {
  const { t } = useTranslation();
  const { sessionId, openCashDrawer, setOpenCashDrawer } = useAuth();

  return (
    <>
      <button
        className={cn(
          "rounded px-3 py-2 text-xs font-semibold uppercase tracking-tight focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-800",
          sessionId ? "bg-green-500 hover:bg-green-400" : "bg-gray-500 hover:bg-gray-400",
        )}
        onClick={() => setOpenCashDrawer(true)}
      >
        {sessionId ? t("cashDrawer.opened") : t("cashDrawer.open")}
      </button>
      <CashDrawerSlider open={openCashDrawer} onClose={setOpenCashDrawer}>
        {sessionId ? (
          <CashDrawerDetail sessionId={sessionId} onClose={() => setOpenCashDrawer(false)} />
        ) : (
          <OpenCashDrawer onClose={() => setOpenCashDrawer(false)} />
        )}
      </CashDrawerSlider>
    </>
  );
}

function MenuSlider({
  sidebarOpen,
  setSidebarOpen,
  onReset,
}: {
  sidebarOpen: boolean;
  setSidebarOpen: (v: boolean) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const { logout, user, isBackoffice, hasPermissions } = useAuth();
  const userType = isBackoffice ? UserType.BACKOFFICE : UserType.POS;
  const { pathname } = useRouter();
  const match = (href: string) => (href === "/" ? pathname === "/" : pathname.startsWith(href));

  return (
    <>
      <Transition.Root as={Fragment} show={sidebarOpen}>
        <Dialog as="div" className="fixed inset-0 z-40 flex" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white pt-5">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute right-0 top-0 -mr-12 pt-2">
                  <button
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <XIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex shrink-0 items-center px-4">
                <Logo className="h-8 w-auto" />
              </div>
              <div className="mt-4 h-0 flex-1 overflow-y-auto pt-2">
                <nav className="space-y-1 px-2">
                  {navigation
                    .filter(
                      ({ users, permissions }) =>
                        users.includes(userType) && hasPermissions(permissions),
                    )
                    .map(({ key, href, icon: Icon }) => (
                      <Link
                        key={key}
                        className={cn(
                          match(href)
                            ? "bg-gray-100 text-gray-900"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                          "group flex items-center rounded-md px-2 py-2 text-base font-medium",
                        )}
                        href={href}
                        onClick={() => {
                          setSidebarOpen(false);
                          onReset();
                        }}
                      >
                        <Icon
                          aria-hidden="true"
                          className={cn(
                            match(href)
                              ? "text-gray-500"
                              : "text-gray-400 group-hover:text-gray-500",
                            "mr-4 h-6 w-6 shrink-0",
                          )}
                        />
                        {t(`layout.nav.${key}`)}
                      </Link>
                    ))}
                </nav>
              </div>
              <div className="block w-full shrink-0 border-t border-gray-200 p-4">
                <p className="text-sm font-medium text-gray-700">{user?.email}</p>
                <button
                  className="flex items-center text-xs font-medium text-gray-500 hover:text-gray-700"
                  onClick={() => logout()}
                >
                  <LogoutIcon aria-hidden="true" className="mr-1 h-4 w-4" />
                  {t("layout.logout")}
                </button>
              </div>
            </div>
          </Transition.Child>
          <div aria-hidden="true" className="w-14 shrink-0">
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

function ChangePDV() {
  const { t } = useTranslation();
  const { posData, setPos } = useAuth();

  return (
    <Menu as="div" className="relative">
      <div>
        <Menu.Button className="flex max-w-xs items-center rounded text-sm text-gray-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-800">
          {posData?.name ?? "PDV"}
          <ChevronDownIcon aria-hidden="true" className="ml-2 h-5 w-5" />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <Menu.Item>
            {({ active }) => (
              <button
                className={cn(
                  active ? "bg-gray-100" : "",
                  "flex w-full items-center px-4 py-2 text-sm text-gray-700",
                )}
                onClick={() => setPos(null)}
              >
                <CollectionIcon aria-hidden="true" className="mr-2 h-5 w-5" />
                {t("layout.changePos")}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function Layout({ children, onReset }: { onReset: () => void; children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, isBackoffice } = useAuth();

  return (
    <div>
      <MenuSlider setSidebarOpen={setSidebarOpen} sidebarOpen={sidebarOpen} onReset={onReset} />
      <div className="flex flex-1 flex-col">
        <div className="sticky top-0 z-10 flex h-16 shrink-0 bg-gray-800 shadow">
          <button
            className="border-r border-gray-700 px-4 text-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            type="button"
            onClick={() => setSidebarOpen(true)}
          >
            <MenuAlt2Icon aria-hidden="true" className="h-6 w-6" />
          </button>
          <div className="ml-4 hidden items-center lg:flex">
            <Logo className="h-8 w-auto" />
          </div>
          <div className="flex flex-1 justify-between px-4">
            <div className="flex flex-1" />
            <div className="ml-4 flex divide-x divide-gray-600 md:ml-6">
              {!isBackoffice ? (
                <div className="flex items-center pr-4">
                  <CashDrawer />
                </div>
              ) : null}
              <div className="flex items-center pl-3 sm:px-3">
                <ChangePDV />
              </div>
              <div className="hidden items-center pl-4 sm:flex">
                <button
                  className="flex max-w-xs items-center rounded text-sm text-gray-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                  onClick={() => setSidebarOpen(true)}
                >
                  {user?.email}
                </button>
              </div>
            </div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export default Layout;
